import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Building2,
  User,
  Phone,
  Mail,
  CreditCard,
  FileText,
  FolderOpen,
  Users,
  Upload,
  Edit,
  Trash2,
  Plus,
  Download,
  Eye,
  AlertTriangle,
  ExternalLink,
  ClipboardList,
  Star,
  Receipt,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { BrandBadge } from '../components/ui/BrandBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { formatCurrency, formatDate } from '../lib/utils';
import { api } from '../core/apiClient';
import apiClient from '../core/apiClient';

// Types
interface Client {
  id: string;
  code: string;
  name: string;
  tradeName?: string;
  cif: string;
  type: 'EMPRESA' | 'AUTONOMO' | 'PARTICULAR' | 'ADMINISTRACION';
  address?: string;
  city?: string;
  postalCode?: string;
  province?: string;
  country: string;
  phone?: string;
  email?: string;
  website?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  paymentTerms: number;
  paymentMethod: string;
  bankAccount?: string;
  creditLimit: number;
  currentBalance: number;
  riskStatus: 'NORMAL' | 'VIGILADO' | 'BLOQUEADO';
  isActive: number;
  notes?: string;
  dropboxUrl?: string;
  oficinaComptable?: string;
  organGestor?: string;
  unitatTramitadora?: string;
  organProponent?: string;
  billToOtherEntity?: boolean;
  billingEntityName?: string;
  billingEntityCif?: string;
  billingEntityAddress?: string;
  billingEntityCity?: string;
  billingEntityPostalCode?: string;
  billingEntityProvince?: string;
  brand?: {
    id: string;
    code: string;
    name: string;
    primaryColor?: string;
  };
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  position?: string;
  department?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  unitatTramitadora?: string;
  isPrimary: boolean;
}

interface Project {
  id: string;
  ecNumber: string;
  name: string;
  etiquetaExpedient?: string | null;
  estat: string;
  createdAt: string;
  project?: { id: string; projectNumber: string } | null;
  budgetsCount?: number;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  totalAmount: number;
  pendingAmount: number;
  status: string;
}

interface ClientDocumentRow {
  id: string;
  name: string;
  fileName: string;
  fileSize: number;
  mimeType?: string;
  createdAt: string;
}

interface ClientSummary {
  totalProjects: number;
  activeProjects: number;
  totalInvoiced: number;
  pendingBalance: number;
  totalDocuments: number;
}

interface Budget {
  id: string;
  budgetNumber: string;
  budgetDate: string;
  validityDate?: string;
  estat: string;
  projecte?: string;
  total: number;
}

// Tabs
type TabId = 'general' | 'contacts' | 'projects' | 'pressupostos' | 'invoices' | 'documents';

const tabs: { id: TabId; label: string; icon: typeof Building2 }[] = [
  { id: 'general', label: 'Dades Generals', icon: Building2 },
  { id: 'contacts', label: 'Contactes', icon: Users },
  { id: 'projects', label: 'Expedients Comercials', icon: FolderOpen },
  { id: 'pressupostos', label: 'Pressupostos', icon: ClipboardList },
  { id: 'invoices', label: 'Facturació', icon: FileText },
  { id: 'documents', label: 'Documents', icon: Upload },
];

// Status configs
const ecStatusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'success' | 'warning' | 'info' }> = {
  OBERT: { label: 'Obert', variant: 'info' },
  DESCARTAT: { label: 'Descartat', variant: 'default' },
  FACTIBLE: { label: 'Factible', variant: 'info' },
  MOLT_FACTIBLE: { label: 'Molt factible', variant: 'info' },
  ACCEPTAT: { label: 'Comanda', variant: 'success' },
  LLIURAT: { label: 'Lliurat', variant: 'warning' },
  TANCAT: { label: 'Facturat', variant: 'secondary' },
};

const invoiceStatusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' }> = {
  DRAFT: { label: 'Esborrany', variant: 'secondary' },
  PENDING: { label: 'Pendent', variant: 'warning' },
  PAID: { label: 'Pagada', variant: 'success' },
  OVERDUE: { label: 'Vencuda', variant: 'destructive' },
  CANCELLED: { label: 'Cancel·lada', variant: 'default' },
};

const riskConfig: Record<string, { label: string; variant: 'default' | 'warning' | 'destructive' }> = {
  NORMAL: { label: 'Normal', variant: 'default' },
  VIGILADO: { label: 'Vigilat', variant: 'warning' },
  BLOQUEADO: { label: 'Bloquejat', variant: 'destructive' },
};

const budgetEstatConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'success' | 'warning' | 'destructive' | 'outline' }> = {
  ESBORRANY: { label: 'Obert',     variant: 'secondary' },
  ENVIAT:    { label: 'Factible',  variant: 'default' },
  ACCEPTAT:  { label: 'Comanda',   variant: 'success' },
  REBUTJAT:  { label: 'Descartat', variant: 'destructive' },
  LLIURAT:   { label: 'Lliurat',   variant: 'default' },
  FACTURAT:  { label: 'Facturat',  variant: 'success' },
};

const paymentMethodLabels: Record<string, string> = {
  TRANSFERENCIA: 'Transferència',
  GIRO: 'Gir bancari',
  TARJETA: 'Targeta de crèdit',
  EFECTIVO: 'Efectiu',
  RECIBO: 'Rebut',
  'REBUT BANCARI': 'Rebut bancari',
};

export default function ClientDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState<TabId>('general');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [client, setClient] = useState<Client | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [documents, setDocuments] = useState<ClientDocumentRow[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docsDragOver, setDocsDragOver] = useState(false);
  const [summary, setSummary] = useState<ClientSummary | null>(null);
  const [dir3Names, setDir3Names] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        // Carregar dades del client des de l'API
        // api.get() ja retorna res.data, que conté { success: true, data: {...} }
        const response = await api.get<{ success: boolean; data: any }>(`/clients/${id}`);
        const clientData = response.data;
        
        if (!clientData || !clientData.id) {
          throw new Error('Client no trobat');
        }
        
        setClient(clientData);

        const dir3Codes = Array.from(new Set(
          [clientData.oficinaComptable, clientData.organGestor, clientData.unitatTramitadora, clientData.organProponent]
            .filter((c): c is string => !!c?.trim())
        ));
        if (dir3Codes.length > 0) {
          api.get<{ success: boolean; data: Record<string, string> }>(`/dir3-units/resolve?codes=${dir3Codes.map(encodeURIComponent).join(',')}`)
            .then((res) => setDir3Names(res.data ?? {}))
            .catch(() => {});
        }

        // Transformar contactes del format API al format del component
        const contactsList = clientData.contacts || [];
        const formattedContacts = contactsList.map((c: any) => ({
          id: c.id,
          firstName: c.name?.split(' ')[0] || c.firstName || '',
          lastName: c.name?.split(' ').slice(1).join(' ') || c.lastName || '',
          position: c.position,
          email: c.email,
          phone: c.phone,
          unitatTramitadora: c.unitatTramitadora,
          isPrimary: c.isPrimary || false,
        }));
        setContacts(formattedContacts);
        
        // Carregar pressupostos del client
        try {
          const budgetsRes = await api.get<{ success: boolean; data: Budget[]; pagination: any }>(
            '/budgets', { clientId: id }
          );
          setBudgets(budgetsRes.data ?? []);
        } catch {
          setBudgets([]);
        }

        // Carregar Expedients Comercials del client
        try {
          const ecRes = await api.get<{ success: boolean; data: Project[] }>(
            '/expedients-comercials', { clientId: id }
          );
          setProjects(ecRes.data ?? []);
        } catch {
          setProjects([]);
        }

        // TODO: Carregar factures des de l'API
        setInvoices([]);
        setSummary({
          totalProjects: 0,
          activeProjects: 0,
          totalInvoiced: clientData.totalInvoiced || 0,
          pendingBalance: clientData.currentBalance || 0,
          totalDocuments: 0,
        });
      } catch (err: any) {
        console.error('[v0] Error loading client:', err);
        setError(err.message || 'Error en carregar les dades del client');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // ── Documents adjunts a la fitxa del client ──────────────────────────────

  const fetchDocuments = useCallback(async () => {
    if (!id) return;
    try {
      const res = await api.get<{ success: boolean; data: ClientDocumentRow[] }>(`/clients/${id}/documents`);
      setDocuments(res.data ?? []);
    } catch { /* no bloquejar la resta de la fitxa si falla el llistat */ }
  }, [id]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleUploadDocument = async (file: File) => {
    if (!id || !file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      await apiClient.post(`/clients/${id}/documents`, fd, { headers: { 'Content-Type': undefined } });
      fetchDocuments();
    } catch (err: any) {
      alert('Error pujant el document: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDocumentsDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDocsDragOver(false);
    Array.from(e.dataTransfer.files ?? []).forEach((f) => handleUploadDocument(f));
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!id) return;
    if (!confirm('Eliminar aquest document?')) return;
    try {
      await api.delete(`/clients/${id}/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch { alert('Error eliminant el document'); }
  };

  // Les rutes de documents requereixen el token d'autenticació (Authorization
  // header), que un <a href> o window.open directe no envia — es baixa com a
  // blob i s'obre/descarrega des d'aquí.
  const handleOpenDocument = async (doc: ClientDocumentRow, download: boolean) => {
    try {
      const res = await apiClient.get(`/clients/${id}/documents/${doc.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: doc.mimeType || 'application/octet-stream' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.name;
        a.click();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert('Error obrint el document');
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error || !client) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertTriangle className="h-12 w-12 text-destructive mb-4" />
        <p className="text-sm font-medium">{error || 'Client no trobat'}</p>
        <button
          onClick={() => navigate('/crm')}
          className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg"
        >
          Tornar al CRM
        </button>
      </div>
    );
  }

  const risk = riskConfig[client.riskStatus] ?? riskConfig['NORMAL'];

  return (
    <div className="space-y-6">
      {/* Capcalera */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate('/crm')}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">{client.name}</h1>
              {client.brand && <BrandBadge name={client.brand.name} color={client.brand.primaryColor} />}
              <Badge variant={risk.variant as any}>
                {risk.label}
              </Badge>
            </div>
            {client.tradeName && (
              <p className="text-muted-foreground">{client.tradeName}</p>
            )}
            <p className="text-sm text-muted-foreground mt-1">
              {client.code} · {client.cif}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/crm/clients/${id}/edit`)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
        >
          <Edit className="h-4 w-4" />
          Editar
        </button>
      </div>

      {/* Stat cards — substitueixen el menú de pestanyes */}
      <div className="grid grid-cols-6 gap-3">
        <button
          onClick={() => setActiveTab('general')}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'general' ? 'border-blue-500 bg-blue-50/50' : 'border-border bg-white hover:shadow-sm'}`}
        >
          <div className="p-1.5 rounded-lg bg-blue-500/10 shrink-0"><Building2 className="h-4 w-4 text-blue-500" /></div>
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">Dades</span>
            <span className="text-xs font-semibold truncate">Generals</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('contacts')}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'contacts' ? 'border-teal-500 bg-teal-50/50' : 'border-border bg-white hover:shadow-sm'}`}
        >
          <div className="p-1.5 rounded-lg bg-teal-500/10 shrink-0"><Users className="h-4 w-4 text-teal-500" /></div>
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">Contactes</span>
            <span className="text-xs font-semibold">{contacts.length || ''}</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('projects')}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'projects' ? 'border-amber-500 bg-amber-50/50' : 'border-border bg-white hover:shadow-sm'}`}
        >
          <div className="p-1.5 rounded-lg bg-amber-500/10 shrink-0"><FolderOpen className="h-4 w-4 text-amber-500" /></div>
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">Expedients</span>
            <span className="text-xs font-semibold">{projects.length || ''}</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('pressupostos')}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'pressupostos' ? 'border-purple-500 bg-purple-50/50' : 'border-border bg-white hover:shadow-sm'}`}
        >
          <div className="p-1.5 rounded-lg bg-purple-500/10 shrink-0"><ClipboardList className="h-4 w-4 text-purple-500" /></div>
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">Pressupostos</span>
            <span className="text-xs font-semibold">{budgets.length || ''}</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('invoices')}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'invoices' ? 'border-green-500 bg-green-50/50' : 'border-border bg-white hover:shadow-sm'}`}
        >
          <div className="p-1.5 rounded-lg bg-green-500/10 shrink-0"><FileText className="h-4 w-4 text-green-500" /></div>
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">Facturació</span>
            <span className="text-xs font-semibold">{summary ? formatCurrency(summary.totalInvoiced) : ''}</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('documents')}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'documents' ? 'border-slate-500 bg-slate-50/50' : 'border-border bg-white hover:shadow-sm'}`}
        >
          <div className="p-1.5 rounded-lg bg-slate-500/10 shrink-0"><Upload className="h-4 w-4 text-slate-500" /></div>
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">Documents</span>
            <span className="text-xs font-semibold">{documents.length || ''}</span>
          </div>
        </button>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {/* Tab: Dades Generals */}
        {activeTab === 'general' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-[12px]">

            {/* Columna esquerra — Dades del Client */}
            <Card className="bg-muted/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Dades del Client
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Codi</p>
                    <p className="font-mono font-medium">{client.code || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Tipus de client</p>
                    <p>{client.type || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">CIF/NIF</p>
                    <p className="font-mono">{client.cif || '—'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Nom / Raó Social</p>
                    <p className="font-medium">{client.name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Nom Comercial</p>
                    <p>{client.tradeName || '—'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Telèfon General</p>
                    <div className="flex items-center gap-1">
                      <p>{client.phone || '—'}</p>
                      {client.phone && (
                        <a href={`tel:${client.phone.replace(/\s/g, '')}`}
                          className="text-muted-foreground hover:text-primary transition-colors" title="Trucar">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Email General</p>
                    <div className="flex items-center gap-1">
                      {client.email
                        ? <a href={`mailto:${client.email}`} className="text-primary hover:underline truncate">{client.email}</a>
                        : <p>—</p>}
                      {client.email && (
                        <a href={`mailto:${client.email}`}
                          className="text-muted-foreground hover:text-primary transition-colors shrink-0" title="Enviar email">
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Web</p>
                  <div className="flex items-center gap-1">
                    {client.website ? (
                      <a href={client.website.startsWith('http') ? client.website : `https://${client.website}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-primary hover:underline truncate">{client.website}</a>
                    ) : <p>—</p>}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">Adreça</p>
                  <p>{client.address || '—'}</p>
                </div>
                <div className="grid grid-cols-6 gap-4">
                  <div className="col-span-2">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Ciutat</p>
                    <p>{client.city || '—'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs font-medium text-muted-foreground mb-1">Província</p>
                    <p>{client.province || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">CP</p>
                    <p>{client.postalCode || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">País</p>
                    <p>{client.country || '—'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Columna dreta — Condicions + Contactes */}
            <div className="space-y-4">
              <Card className="bg-muted/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="h-5 w-5" />
                    Condicions Comercials
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Mètode de Pagament</p>
                      <p>{paymentMethodLabels[client.paymentMethod] || client.paymentMethod || '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Dies de Pagament</p>
                      <p>{client.paymentTerms != null ? `${client.paymentTerms} dies` : '—'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">Límit de Crèdit</p>
                      <p>{formatCurrency(client.creditLimit)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">IBAN</p>
                      <p className="font-mono">{client.bankAccount || '—'}</p>
                    </div>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
                    <p className="whitespace-pre-wrap">{client.notes || '—'}</p>
                  </div>
                  {client.dropboxUrl && (() => {
                    try {
                      const loc: { name: string; link: string } = JSON.parse(client.dropboxUrl!);
                      return (
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Localització Dropbox</p>
                          <a href={loc.link} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-background hover:bg-muted transition-colors">
                            <FolderOpen className="h-4 w-4 text-blue-500 shrink-0" />
                            <span className="truncate">{loc.name}</span>
                            <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0 ml-auto" />
                          </a>
                        </div>
                      );
                    } catch { return null; }
                  })()}
                </CardContent>
              </Card>

              {/* Dades de facturació — codis DIR3 sempre visibles; l'entitat
                  alternativa nomes si el client hi factura */}
              <Card className="bg-muted/30">
                <CardHeader className="py-3">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Receipt className="h-4 w-4" />
                    Dades de facturació
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  <div className="grid grid-cols-4 gap-4">
                    {([
                      ['oficinaComptable', 'Oficina Comptable'],
                      ['organGestor', 'Òrgan Gestor'],
                      ['unitatTramitadora', 'Unitat Tramitadora'],
                      ['organProponent', 'Òrgan Proponent'],
                    ] as const).map(([field, label]) => {
                      const value = client[field];
                      const resolvedName = value ? dir3Names[value] : undefined;
                      return (
                        <div key={field}>
                          <p className="text-xs font-medium text-muted-foreground mb-1">{label}</p>
                          <p className="font-mono truncate" title={resolvedName ? `${value} - ${resolvedName}` : value || undefined}>
                            {value ? (resolvedName ? `${value} - ${resolvedName}` : value) : '—'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                  {client.billToOtherEntity && (
                    <div className="space-y-3 pt-2 border-t border-border">
                      <p className="text-xs text-muted-foreground">Es factura a nom d'una altra entitat.</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">Nom de l'entitat</p>
                          <p>{client.billingEntityName || '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-1">CIF/NIF</p>
                          <p className="font-mono">{client.billingEntityCif || '—'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">Adreça</p>
                        <p>
                          {[client.billingEntityAddress, [client.billingEntityPostalCode, client.billingEntityCity].filter(Boolean).join(' '), client.billingEntityProvince]
                            .filter(Boolean).join(', ') || '—'}
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Persones de Contacte */}
              <Card className="bg-muted/30">
                <CardHeader className="py-3">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4" />
                    Persones de Contacte
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  {contacts.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2">Cap contacte registrat.</p>
                  ) : (
                    <div className="space-y-2">
                      {contacts.map(contact => (
                        <div key={contact.id} className="flex items-center gap-2 border rounded-lg px-3 py-2 bg-background">
                          {contact.isPrimary
                            ? <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400 shrink-0" />
                            : <span className="w-3.5 shrink-0" />}
                          <span className="flex-1 font-medium truncate">{contact.firstName} {contact.lastName}</span>
                          {contact.position && (
                            <span className="text-muted-foreground truncate">{contact.position}</span>
                          )}
                          {contact.phone && (
                            <a href={`tel:${contact.phone.replace(/\s/g, '')}`}
                              className="text-muted-foreground hover:text-primary shrink-0" title={contact.phone}>
                              <Phone className="h-3 w-3" />
                            </a>
                          )}
                          {contact.email && (
                            <a href={`mailto:${contact.email}`}
                              className="text-muted-foreground hover:text-primary shrink-0" title={contact.email}>
                              <Mail className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Tab: Contactes */}
        {activeTab === 'contacts' && (
          <Card className="rounded-xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Persones de Contacte</CardTitle>
                <button className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm">
                  <Plus className="h-4 w-4" />
                  Afegir Contacte
                </button>
              </div>
            </CardHeader>
            <CardContent>
              {contacts.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No hi ha contactes registrats
                </p>
              ) : (
                <div className="space-y-4">
                  {contacts.map(contact => (
                    <div key={contact.id} className="flex items-start justify-between p-4 border rounded-lg">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{contact.firstName} {contact.lastName}</p>
                            {contact.isPrimary && (
                              <Badge variant="secondary" className="text-xs">Principal</Badge>
                            )}
                          </div>
                          {contact.position && (
                            <p className="text-sm text-muted-foreground">{contact.position}</p>
                          )}
                          {contact.department && (
                            <p className="text-xs text-muted-foreground">{contact.department}</p>
                          )}
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            {contact.email && (
                              <a href={`mailto:${contact.email}`} className="flex items-center gap-1 text-primary hover:underline">
                                <Mail className="h-3 w-3" />
                                {contact.email}
                              </a>
                            )}
                            {contact.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                {contact.phone}
                              </span>
                            )}
                          </div>
                          {contact.unitatTramitadora && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Unitat Tramitadora: <span className="font-mono">{contact.unitatTramitadora}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button className="p-2 hover:bg-muted rounded-lg">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="p-2 hover:bg-muted rounded-lg text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tab: Expedients Comercials */}
        {activeTab === 'projects' && (
          <Card className="rounded-xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Expedients Comercials</CardTitle>
                <button
                  onClick={() => navigate(`/crm/expedients-comercials/new?clientId=${id}`)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm"
                >
                  <Plus className="h-4 w-4" />
                  Nou Expedient Comercial
                </button>
              </div>
            </CardHeader>
            <CardContent>
              {projects.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No hi ha expedients comercials registrats
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Expedient Comercial</TableHead>
                      <TableHead>Nom</TableHead>
                      <TableHead>Estat</TableHead>
                      <TableHead>Pressupostos</TableHead>
                      <TableHead>Expedient de Taller</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {projects.map(ec => {
                      const status = ecStatusConfig[ec.estat] || { label: ec.estat, variant: 'default' };
                      return (
                        <TableRow key={ec.id}>
                          <TableCell className="font-mono text-sm">{ec.ecNumber}</TableCell>
                          <TableCell className="font-medium">{ec.etiquetaExpedient || ec.name}</TableCell>
                          <TableCell>
                            <Badge variant={status.variant as any}>{status.label}</Badge>
                          </TableCell>
                          <TableCell>{ec.budgetsCount ?? 0}</TableCell>
                          <TableCell>{ec.project?.projectNumber ?? '—'}</TableCell>
                          <TableCell>{formatDate(ec.createdAt)}</TableCell>
                          <TableCell>
                            <button
                              onClick={() => navigate(`/crm/expedients-comercials/${ec.id}/edit`)}
                              className="p-2 hover:bg-muted rounded-lg"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tab: Pressupostos */}
        {activeTab === 'pressupostos' && (
          <Card className="rounded-xl">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Pressupostos</CardTitle>
                <button
                  onClick={() => navigate(`/crm/pressupostos/new?clientId=${id}`)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm"
                >
                  <Plus className="h-4 w-4" />
                  Nou Pressupost
                </button>
              </div>
            </CardHeader>
            <CardContent>
              {budgets.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No hi ha pressupostos registrats
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Número</TableHead>
                      <TableHead>Projecte</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Validesa</TableHead>
                      <TableHead>Estat</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {budgets.map(budget => {
                      const estat = budgetEstatConfig[budget.estat] || { label: budget.estat, variant: 'default' };
                      return (
                        <TableRow key={budget.id}>
                          <TableCell className="font-mono text-sm">{budget.budgetNumber}</TableCell>
                          <TableCell className="font-medium">{budget.projecte || '—'}</TableCell>
                          <TableCell>{formatDate(budget.budgetDate)}</TableCell>
                          <TableCell>{budget.validityDate ? formatDate(budget.validityDate) : '—'}</TableCell>
                          <TableCell>
                            <Badge variant={estat.variant as any}>{estat.label}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(budget.total)}</TableCell>
                          <TableCell>
                            <button
                              onClick={() => navigate(`/crm/pressupostos/${budget.id}/edit`)}
                              className="p-2 hover:bg-muted rounded-lg"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tab: Facturació */}
        {activeTab === 'invoices' && (
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle>Historial de Facturació</CardTitle>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No hi ha factures registrades
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Numero</TableHead>
                      <TableHead>Data Emissio</TableHead>
                      <TableHead>Venciment</TableHead>
                      <TableHead className="text-right">Import</TableHead>
                      <TableHead className="text-right">Pendent</TableHead>
                      <TableHead>Estat</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map(invoice => {
                      const status = invoiceStatusConfig[invoice.status] || { label: invoice.status, variant: 'default' };
                      return (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-mono text-sm">{invoice.invoiceNumber}</TableCell>
                          <TableCell>{formatDate(invoice.issueDate)}</TableCell>
                          <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(invoice.totalAmount)}</TableCell>
                          <TableCell className={`text-right font-mono ${invoice.pendingAmount > 0 ? 'text-warning' : ''}`}>
                            {formatCurrency(invoice.pendingAmount)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={status.variant as any}>{status.label}</Badge>
                          </TableCell>
                          <TableCell>
                            <button className="p-2 hover:bg-muted rounded-lg">
                              <Download className="h-4 w-4" />
                            </button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tab: Documents */}
        {activeTab === 'documents' && (
          <Card className="rounded-xl">
            <CardHeader>
              <CardTitle>Documents generals Client</CardTitle>
              <CardDescription>
                Contractes, mandats SEPA, documents legals i altres arxius
              </CardDescription>
            </CardHeader>
            <CardContent>
              <label
                onDragOver={(e) => { e.preventDefault(); setDocsDragOver(true); }}
                onDragLeave={() => setDocsDragOver(false)}
                onDrop={handleDocumentsDrop}
                className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-12 cursor-pointer transition-colors ${
                  docsDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <Upload className="h-12 w-12 text-muted-foreground" />
                <p className="text-muted-foreground">
                  {uploadingDoc ? 'Pujant...' : 'Arrossega arxius aquí o fes clic per pujar'}
                </p>
                <input
                  type="file"
                  className="hidden"
                  disabled={uploadingDoc}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUploadDocument(file);
                    e.target.value = '';
                  }}
                />
              </label>

              {documents.length > 0 && (
                <div className="space-y-2 mt-4">
                  {documents.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                      <div className="flex items-center gap-3 min-w-0">
                        <FileText className="h-8 w-8 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium truncate" title={doc.name}>{doc.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatFileSize(doc.fileSize)} · {formatDate(doc.createdAt)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button type="button" title="Visualitzar" onClick={() => handleOpenDocument(doc, false)}
                          className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-blue-600">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button type="button" title="Descarregar" onClick={() => handleOpenDocument(doc, true)}
                          className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-green-600">
                          <Download className="h-4 w-4" />
                        </button>
                        <button type="button" title="Eliminar" onClick={() => handleDeleteDocument(doc.id)}
                          className="p-2 hover:bg-muted rounded-lg text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
